// §5.4 / §5.2 — edge snapping, the live zone overlay, and Snap Groups.
//
// Two rules from §5.2 shape everything here:
//
//  1. **The overlay is drawn directly.** Highlighting a zone during a drag never goes through
//     React state — one element, mutated in place, and only when the armed zone actually changes.
//  2. **The store is written once.** The snap lands on `pointerup`, in the same single commit the
//     drag would otherwise have made.
//
// Which zone an edge means is the *skin's* decision (§5.4's `WindowManagerPolicy`), so the policy
// is injected: `kernel/**` may not import `skins/**` (§6.5).
import type { Edge, Rect, SnapLayout, SnapZone, Win, WindowManagerPolicy } from '@hangar/shared';
import type { WmStore } from './store';
import { zoneRect } from './geometry';

/** How close to an edge the pointer must come before a zone arms. */
export const EDGE_THRESHOLD = 12;

export interface SnapTarget {
  layoutId: string;
  zoneId: string;
  rect: Rect;
}

/**
 * The element the overlay writes to. Narrow and structural, like `DragElement` (§5.2), so the
 * whole engine is testable without a browser.
 */
export interface OverlayElement {
  style: { transform: string; width: string; height: string; opacity: string };
  dataset: Record<string, string | undefined>;
}

/**
 * Which display edge the pointer is at, or `null`. Corners are reported as corners — the house
 * skin folds them onto a half, the windows11 skin (M7) will make them quadrants, and that choice
 * is the policy's, not this function's.
 */
export function edgeAt(
  point: { x: number; y: number },
  area: Rect,
  threshold: number = EDGE_THRESHOLD,
): Edge | null {
  const left = point.x <= area.x + threshold;
  const right = point.x >= area.x + area.w - threshold;
  const top = point.y <= area.y + threshold;
  const bottom = point.y >= area.y + area.h - threshold;

  // Outside the area entirely is not an edge: the pointer has left the desktop.
  if (point.x < area.x - threshold || point.x > area.x + area.w + threshold) return null;
  if (point.y < area.y - threshold || point.y > area.y + area.h + threshold) return null;

  if (top && left) return 'topLeft';
  if (top && right) return 'topRight';
  if (bottom && left) return 'bottomLeft';
  if (bottom && right) return 'bottomRight';
  if (left) return 'left';
  if (right) return 'right';
  if (top) return 'top';
  if (bottom) return 'bottom';
  return null;
}

/**
 * §5.4 — "windows snapped into the same `SnapLayout` share a `groupId`". Derived rather than
 * minted, so the group survives a reload without being persisted separately (§5.6) and two
 * windows that snap into the same layout minutes apart still find each other.
 */
export function groupIdFor(desktopId: string, layoutId: string): string {
  return `${desktopId}:${layoutId}`;
}

/** The layout a zone id belongs to, searching the policy's own table. */
export function layoutOf(layouts: readonly SnapLayout[], zoneId: string): SnapLayout | null {
  return layouts.find((layout) => layout.zones.some((zone) => zone.id === zoneId)) ?? null;
}

export function targetFor(
  policy: WindowManagerPolicy,
  edge: Edge,
  win: Win,
  area: Rect,
): SnapTarget | null {
  const zone: SnapZone | null = policy.onEdgeDrag(edge, win, area);
  if (!zone) return null;
  const layout = layoutOf(policy.snapZones(area), zone.id);
  if (!layout) return null;
  return { layoutId: layout.id, zoneId: zone.id, rect: zoneRect(zone.rect, area) };
}

export interface SnapService {
  /** The overlay element, handed over by the host once it exists. `null` detaches it. */
  attach(element: OverlayElement | null): void;
  /** Arm the engine for a move drag. Does nothing for a resize. */
  begin(winId: string): void;
  /** Called with the pointer in desktop space on every drag frame. */
  update(point: { x: number; y: number }): void;
  /** What would be committed right now. */
  armed(): SnapTarget | null;
  /**
   * Land the snap. Returns `true` when it took the commit — the caller must then *not* write the
   * dragged rect, because the window belongs to a zone now.
   */
  commit(): boolean;
  cancel(): void;
  /**
   * §5.4 — snap a window into a named zone with no drag involved: the Snap Layouts picker, Snap
   * Assist, and the `Meta+Arrow` chords all land here. Returns false when the active policy has
   * no such zone, which is how a skin without that layout simply declines.
   */
  snapTo(winId: string, layoutId: string, zoneId: string): boolean;
  /** §6.4 — what `Meta+Arrow` means, asked of the policy exactly as an edge drag would be. */
  snapEdge(winId: string, edge: Edge): boolean;
  /** Every window in a snap group, in z order. */
  members(groupId: string): Win[];
  /** The groups with at least one window on a desktop, for the bar (§5.4). */
  groupsOn(desktopId: string): Array<{ groupId: string; layoutId: string; windows: Win[] }>;
  restoreGroup(groupId: string): void;
  minimizeGroup(groupId: string): void;
}

export interface SnapServiceOptions {
  wm: WmStore;
  /** The active skin's policy (§5.4). Read on every call so a skin change takes effect at once. */
  policy: () => WindowManagerPolicy;
  workArea: () => Rect;
  threshold?: number;
}

export function createSnapService(options: SnapServiceOptions): SnapService {
  const { wm } = options;
  const threshold = options.threshold ?? EDGE_THRESHOLD;

  let overlay: OverlayElement | null = null;
  let dragging: string | null = null;
  let target: SnapTarget | null = null;

  function paint(): void {
    if (!overlay) return;
    if (!target) {
      overlay.style.opacity = '0';
      overlay.dataset['visible'] = 'false';
      overlay.dataset['zone'] = '';
      return;
    }
    const { rect } = target;
    overlay.style.transform = `translate3d(${rect.x}px, ${rect.y}px, 0)`;
    overlay.style.width = `${rect.w}px`;
    overlay.style.height = `${rect.h}px`;
    overlay.style.opacity = '1';
    overlay.dataset['visible'] = 'true';
    overlay.dataset['zone'] = target.zoneId;
  }

  function disarm(): void {
    dragging = null;
    target = null;
    paint();
  }

  return {
    attach(element) {
      overlay = element;
      paint();
    },

    begin(winId) {
      dragging = winId;
      target = null;
      paint();
    },

    update(point) {
      if (dragging === null) return;
      const win = wm.get(dragging);
      if (!win) return;
      const area = options.workArea();
      const edge = edgeAt(point, area, threshold);
      const next = edge === null ? null : targetFor(options.policy(), edge, win, area);

      // Only touch the DOM when the armed zone actually changes — a pointer delivers frames far
      // faster than a zone can change, and the overlay has a transition on it.
      if (next?.zoneId === target?.zoneId) return;
      target = next;
      paint();
    },

    armed: () => (target ? { ...target, rect: { ...target.rect } } : null),

    commit() {
      const winId = dragging;
      const landing = target;
      disarm();
      if (winId === null || landing === null) return false;
      const win = wm.get(winId);
      if (!win) return false;
      wm.setSnap(
        winId,
        { zoneId: landing.zoneId, groupId: groupIdFor(win.desktopId, landing.layoutId) },
        landing.rect,
      );
      return true;
    },

    cancel: disarm,

    snapTo(winId, layoutId, zoneId) {
      const win = wm.get(winId);
      if (!win) return false;
      const area = options.workArea();
      const layout = options.policy()
        .snapZones(area)
        .find((candidate) => candidate.id === layoutId);
      const zone = layout?.zones.find((candidate) => candidate.id === zoneId);
      if (!layout || !zone) return false;
      wm.setSnap(
        winId,
        { zoneId: zone.id, groupId: groupIdFor(win.desktopId, layout.id) },
        zoneRect(zone.rect, area),
      );
      return true;
    },

    snapEdge(winId, edge) {
      const win = wm.get(winId);
      if (!win) return false;
      const landing = targetFor(options.policy(), edge, win, options.workArea());
      if (!landing) return false;
      wm.setSnap(
        winId,
        { zoneId: landing.zoneId, groupId: groupIdFor(win.desktopId, landing.layoutId) },
        landing.rect,
      );
      return true;
    },

    members(groupId) {
      return wm.list().filter((win) => win.snap?.groupId === groupId);
    },

    groupsOn(desktopId) {
      const groups = new Map<string, { groupId: string; layoutId: string; windows: Win[] }>();
      for (const win of wm.list()) {
        if (win.desktopId !== desktopId || !win.snap) continue;
        const groupId = win.snap.groupId;
        const existing = groups.get(groupId);
        if (existing) existing.windows.push(win);
        else {
          groups.set(groupId, {
            groupId,
            // The layout id is the tail of the derived group id (`<desktop>:<layout>`).
            layoutId: groupId.slice(groupId.indexOf(':') + 1),
            windows: [win],
          });
        }
      }
      return [...groups.values()];
    },

    restoreGroup(groupId) {
      // A snapped window that was minimized comes back *snapped*, not to its pre-snap rect —
      // restoring a group means showing it, not dismantling it (§5.4).
      for (const win of this.members(groupId)) {
        if (win.state === 'minimized') wm.restore(win.id);
      }
    },

    minimizeGroup(groupId) {
      for (const win of this.members(groupId)) wm.minimize(win.id);
    },
  };
}
